var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { useCallback, useEffect, useLayoutEffect } from 'react';
import { cx, css as emotionCss } from 'emotion';
import FocusTrap from 'focus-trap-react';
import { getChildByType, removeChildrenByType } from 'react-nanny';
import { BorderlessButton } from '@citrus/button';
import { Icon } from '@citrus/icon';
import { getAutomation, getDebug, getStyleNames, useCitrusDebug, useEventListener, } from '@citrus/runtime';
import { useLocalization, useTheme } from '@citrus/user';
import { isNotNilOrEmpty, escapeKeyPressed } from '@pcty/utils';
import { setScrollbar, resetScrollbar } from './_utils';
import { Body, Header, HeaderButtonContainer, HeaderText, Large, Modal as Medium, Overlay, Small, } from './Modal.styled';
import { headerIcon, open as openCss, overlayOpen } from '../styles/legacy';
import { headerIcon as headerIconCitron, open as openCssCitron, overlayOpen as overlayOpenCitron, } from '../styles/citron';
import localizationMap from '../localization';
export var modalOverlayOpenBodyIdentifier = 'pcty-fa915ce-modal-overlay-open';
import ModalFooter from './ModalFooter';
import ModalHeader from './ModalHeader';
var VERSION = '5.9.0';
var Modal = function (_a) {
    var _b, _c;
    var automationId = _a.automationId, children = _a.children, className = _a.className, css = _a.css, focusTrapProps = _a.focusTrapProps, headerText = _a.headerText, id = _a.id, _d = _a.ignoreCancelActions, ignoreCancelActions = _d === void 0 ? false : _d, onCancel = _a.onCancel, _e = _a.open, open = _e === void 0 ? false : _e, _f = _a.size, size = _f === void 0 ? 'medium' : _f, _g = _a.__TYPE, __TYPE = _g === void 0 ? 'Modal' : _g, other = __rest(_a, ["automationId", "children", "className", "css", "focusTrapProps", "headerText", "id", "ignoreCancelActions", "onCancel", "open", "size", "__TYPE"]);
    var debug = useCitrusDebug();
    var citrus2022 = useTheme().citrus2022;
    var isCitron = citrus2022;
    var lang = useLocalization(localizationMap);
    var handleKeyPress = useCallback(function (e) {
        if (!open || ignoreCancelActions)
            return;
        var target = e.target;
        if (escapeKeyPressed(e) &&
            id === target.id && // only handle for this id/element
            !window.citrusFlyoutOpen) {
            onCancel({ event: e, onOverlay: false });
            e.preventDefault();
        }
    }, [id, ignoreCancelActions, onCancel, open]);
    var onClickOverlay = useCallback(function (event) {
        if (!open || ignoreCancelActions)
            return;
        onCancel({ event: event, onOverlay: true });
        event.preventDefault();
    }, [ignoreCancelActions, onCancel, open]);
    useEventListener('keydown', handleKeyPress);
    // unmount
    useEffect(function () { return function () {
        resetScrollbar();
        document.body.classList.remove(modalOverlayOpenBodyIdentifier);
        window.citrusModalOpen = false;
    }; }, []);
    useLayoutEffect(function () {
        if (open) {
            document.body.classList.add(modalOverlayOpenBodyIdentifier);
            window.citrusModalOpen = true;
        }
        if (open) {
            setScrollbar();
            return;
        }
        resetScrollbar();
        document.body.classList.remove(modalOverlayOpenBodyIdentifier);
        /*
          NOTE:
    
          Timeout gives Drawer event listener for the escape key time execute while still believing the modal is open. Without the timeout,
          the window variable is updated before the event listener can trigger which means it will think the modal is closed and that the
          escape key was meant for the drawer.
        */
        setTimeout(function () {
            window.citrusModalOpen = false;
        }, 100);
    }, [open]);
    var _openCss = isCitron ? openCssCitron : openCss;
    var _overlayOpen = isCitron ? overlayOpenCitron : overlayOpen;
    var emotionObj = (_b = {},
        _b[emotionCss(_openCss)] = open,
        _b[emotionCss(css)] = isNotNilOrEmpty(css),
        _b);
    var classes = cx(emotionObj, className || '');
    var styleNames = {
        openCss: emotionObj[emotionCss(_openCss)],
    };
    var emotionObjOverlay = (_c = {},
        _c[emotionCss(_overlayOpen)] = open,
        _c);
    var classesOverlay = cx(emotionObjOverlay);
    var styleNamesOverlay = {
        overlayOpen: emotionObj[emotionCss(_overlayOpen)],
    };
    var Component = getComponent(size);
    var _headerIcon = isCitron ? headerIconCitron : headerIcon;
    var renderHeader = function () {
        var customHeader = getChildByType(children, ['ModalHeader']);
        if (isNotNilOrEmpty(customHeader)) {
            return customHeader;
        }
        return (React.createElement(Header, { "data-automation-id": "".concat(automationId, "-header") },
            React.createElement(HeaderText, null, headerText),
            React.createElement(HeaderButtonContainer, null, renderButton(automationId, id, onCancel, lang, _headerIcon))));
    };
    // Visibility style attribute allows screen readers to ignore modal in dom
    return (React.createElement(FocusTrap, __assign({ active: open, focusTrapOptions: { allowOutsideClick: true, initialFocus: "#".concat(id), returnFocusOnDeactivate: false } }, focusTrapProps),
        React.createElement("div", { style: { visibility: open ? 'visible' : 'hidden', transition: "visibility 0s ".concat(open ? 0 : 0.5, "s") } },
            React.createElement(Overlay, __assign({ className: classesOverlay, id: id + '-overlay', onClick: onClickOverlay, styleNames: getStyleNames(styleNamesOverlay) }, getAutomation(automationId, 'modal-overlay'))),
            React.createElement(Component, __assign({ className: classes, id: id, styleNames: getStyleNames(styleNames), tabIndex: 0 }, other, getAutomation(automationId), getDebug(debug, __TYPE, VERSION), { role: "dialog" }),
                renderHeader(),
                getChildByType(children, ['Banner', 'PctyBanner']),
                React.createElement(Body, null, removeChildrenByType(children, ['Banner', 'ModalFooter', 'PctyBanner', 'ModalHeader'])),
                getChildByType(children, ['ModalFooter'])))));
};
var renderButton = function (automationId, id, onCancel, lang, headerIcon) { return (React.createElement(BorderlessButton, __assign({}, getAutomation(automationId, 'close'), { "aria-label": lang.cancel, "data-dismiss": "modal", onClick: function (e) { return onCancel({ onOverlay: false, event: e }); }, size: 'medium' }, getAutomation(id, 'close')),
    React.createElement(Icon, { "aria-label": lang.cancel, className: emotionCss(headerIcon), name: "delete" }))); };
var getComponent = function (size) {
    switch (size) {
        case 'small':
            return Small;
        case 'medium':
        default:
            return Medium;
        case 'large':
            return Large;
    }
};
Modal.defaultProps = {
    __TYPE: 'Modal',
};
export default Object.assign(Modal, { Footer: ModalFooter, Header: ModalHeader });
